import { Controller } from '@hotwired/stimulus';
import TomSelect from 'tom-select';
import AutoSave from 'stimulus/auto_save';
import describeBy from 'stimulus/describe_by';
import 'tom-select/dist/css/tom-select.css';

// Remote option loading, as the user types:
// - `remote-url` value: GET <url>?q= returning [{ value, label }] or the
//   typeahead [{ id, text }] shape.
// - `graphql-query` value: POST /graphql (or `remote-url` if set) with
//   { query, variables: { query: <input> } }; `graphql-path` (e.g.
//   "tags.nodes") locates the option nodes ({ id, name }) in the response.
//
// Picks re-dispatch as `select-field-component:change` ({ value, label })
// for composing controllers (selected-chips-component), which also use the
// reset/take/release API to keep already-picked options out of the menu.
const ABOVE_CLASS = 'ui-field--dropdown-above';
const NO_RESULTS = '<div class="no-results">No results found</div>';

export default class extends Controller {
  static targets = ['select', 'clear', 'action'];

  static values = {
    allowEmptyOption: Boolean,
    noMatchLabel: String,
    remoteUrl: String,
    graphqlQuery: String,
    graphqlPath: String,
    autoSaveUrl: String,
    messageId: String,
    clearable: { type: Boolean, default: true },
    createLabel: String,
    freeText: Boolean,
    takenLabels: Array,
    debounce: { type: Number, default: 500 }
  };

  connect() {
    this.failedQueries = new Set();
    // Turbo snapshots the page as-is; destroy() restores the original
    // <select> so the cached copy re-enhances cleanly on restore.
    this.handleBeforeCache = () => this.teardown();
    document.addEventListener('turbo:before-cache', this.handleBeforeCache);
    this.autoSave = new AutoSave({
      url: this.autoSaveUrlValue,
      name: this.selectTarget.name,
      debounce: this.debounceValue,
      label: this.identifier,
      element: this.element,
      onSaved: value => this.dispatch('saved', { detail: { value } }),
      onError: message => this.showError(message)
    });
    this.tomSelect = new TomSelect(this.selectTarget, this.settings());
    this.syncAria();

    // A field near the bottom of the page would otherwise show only its first option or two.
    this.tomSelect.on('dropdown_open', (dropdown) => this.placeDropdown(dropdown));
    this.tomSelect.on('dropdown_close', () => this.element.classList.remove(ABOVE_CLASS));

    this.syncClear();
  }

  disconnect() {
    document.removeEventListener('turbo:before-cache', this.handleBeforeCache);
    this.autoSave?.cancel();
    this.teardown();
  }

  clear(event) {
    event.preventDefault();
    this.tomSelect.clear();
    this.tomSelect.setTextboxValue('');
    this.selectTarget.dispatchEvent(new Event('change', { bubbles: true }));
    this.dispatch('change', { detail: { value: '', label: '' } });
    this.syncClear();
  }

  // Clicks on the control's own chrome (chevron, padding). Tom Select handles
  // the ones inside its wrapper and the clear button has its own action.
  toggle(event) {
    const ts = this.tomSelect;
    if (!ts || ts.isLocked || event.target.closest('.ts-wrapper, .ui-field__clear')) return;

    if (ts.isFocused && ts.isOpen) {
      ts.blur();
    } else {
      ts.focus();
    }
  }

  syncClear() {
    if (!this.hasClearTarget) return;

    this.clearTarget.hidden = !this.tomSelect.getValue();
  }

  // --- API for composing controllers ---

  // Clears text + selection without dispatching a change.
  reset() {
    this.tomSelect.clear(true);
    this.tomSelect.setTextboxValue('');
  }

  take(value) {
    const option = this.tomSelect.options[value];
    if (option) this.rememberTaken(option.text);
    this.tomSelect.removeOption(value);
  }

  release(value, label) {
    this.tomSelect.addOption({ value, text: label });
  }

  // Tom Select hides the <select> and hands focus to a combobox of its own, which only copies
  // `aria-label`. What the server rendered for the select has to reach the element a screen
  // reader is actually on.
  syncAria() {
    ['aria-describedby', 'aria-invalid'].forEach((name) => {
      const value = this.selectTarget.getAttribute(name);
      if (value === null) {
        this.tomSelect.focus_node.removeAttribute(name);
      } else {
        this.tomSelect.focus_node.setAttribute(name, value);
      }
    });
  }

  settings() {
    const settings = {
      maxItems: 1,
      maxOptions: null,
      allowEmptyOption: this.allowEmptyOptionValue,
      placeholder: this.selectTarget.dataset.placeholder,
      onChange: (value) => this.change(value),
    };

    // A field you pick from rather than type into gets no text box: it already wears the
    // pointer and hides the caret, and typing into a closed list of two only looked like
    // free text. Anything that is answered by typing -- a search input, a remote or
    // GraphQL source, a create row -- keeps its input.
    if (!this.typeable) settings.controlInput = null;

    // Left to Tom Select's own rendering unless this field asks for something it does not
    // do: a field that carries neither images nor an action is untouched by any of it.
    const render = {};

    if (this.hasImages) {
      // Tom Select looks an option's extra json up in its dataset, so this is the key, not
      // the attribute name.
      settings.dataAttr = 'data';
      Object.assign(render, this.optionRenderers());
    }

    if (this.hasActionTarget) render.dropdown = () => this.dropdownWithAction();

    // An offer for text that matched nothing, on Tom Select's own create row, which gives it
    // keyboard selection. A no-match row tells the page and adds nothing — propose a skill,
    // say. Free text becomes the value, or reaches the chips of a multiple select, which add it.
    if (this.hasCreateLabelValue || this.hasNoMatchLabelValue) {
      settings.create = (input, callback) => {
        if (this.hasNoMatchLabelValue) {
          this.dispatch('no-match', { detail: { query: input } });
          return callback();
        }
        if (this.freeTextValue) return callback({ value: input, text: input });

        this.dispatch('create', { detail: { query: input } });
        return callback();
      };
      // Tom Select would otherwise offer the row whenever the text is not an option's
      // *value*, so it would sit under a perfectly good match.
      settings.createFilter = (input) => this.noExactMatch(input);
      Object.assign(render, {
        option_create: (data, escape) =>
          `<div class="create ui-field__create">${escape(this.rowLabel).replace('%{query}', escape(data.input))}</div>`,
        // The create row already says the text found nothing, so Tom Select's own row
        // would sit under it saying it again. It still speaks for a search that failed
        // or has not finished, which is when the create row stays away.
        no_results: (data) => (this.noExactMatch(data.input) ? null : NO_RESULTS)
      });
    }

    if (Object.keys(render).length) settings.render = render;

    // A required answer the rest of the form branches on can be changed but not taken back,
    // so Backspace must not empty it. The page can still clear it through `clear`.
    if (!this.clearableValue) settings.onDelete = () => false;

    if (this.hasGraphqlQueryValue || this.hasRemoteUrlValue) {
      settings.shouldLoad = (query) => query.trim().length > 0;
      settings.load = this.hasGraphqlQueryValue
        ? (query, callback) => this.loadGraphqlOptions(query, callback)
        : (query, callback) => this.loadOptions(query, callback);
    }

    return settings;
  }

  // The row offered for text that matched nothing: one the page handles, or free text.
  get rowLabel() {
    return this.hasNoMatchLabelValue ? this.noMatchLabelValue : this.createLabelValue;
  }

  get typeable() {
    return this.element.classList.contains('ui-field--searchable') || this.hasNoMatchLabelValue ||
           this.hasRemoteUrlValue || this.hasGraphqlQueryValue || this.hasCreateLabelValue;
  }

  get hasImages() {
    return this.selectTarget.querySelector('option[data-data]') !== null;
  }

  // An option showing a face or a logo draws the same way in the menu and in the control,
  // so the chosen one still carries it.
  optionRenderers() {
    const withImage = (data, escape) => {
      const label = escape(data.text);
      if (!data.image) return `<div>${label}</div>`;

      return `<div class="ui-field__option">` +
             `<img class="ui-field__option-image" src="${escape(data.image)}" alt="">` +
             `<span class="ui-field__option-label">${label}</span></div>`;
    };

    return { option: withImage, item: withImage };
  }

  // Tom Select builds its list inside whatever this returns, so a row placed here stays
  // above the options instead of scrolling away with them.
  dropdownWithAction() {
    const dropdown = document.createElement('div');
    const action = this.actionTarget.content.firstElementChild.cloneNode(true);
    action.classList.add('ui-field__action');
    // Held back so the field keeps its focus until the click lands.
    action.addEventListener('mousedown', event => event.preventDefault());
    action.addEventListener('click', () => this.tomSelect.close());
    dropdown.append(action);

    return dropdown;
  }

  // react-select's rule, which the field this replaced used: the row is offered unless the
  // text already names an option. Waiting for zero matches instead would hide it behind a
  // loose one — the tag search is a trigram search, so "SvelteKit" finds "Svelte" and the
  // proposal it should lead to becomes unreachable. Options taken out of the menu for a chip
  // still count, or an answer already given would look like one that does not exist. A query
  // whose search failed is not an answer either way.
  noExactMatch(input) {
    if (!this.tomSelect || this.tomSelect.loading) return false;

    const query = this.normalize(input);
    if (!query || this.failedQueries.has(query)) return false;

    return !this.labels().has(query);
  }

  // Back into the attribute rather than an instance field: a page restored from cache brings
  // its chips with it but rebuilds Tom Select from the server's options, and the labels of
  // whatever was picked in between would be gone.
  rememberTaken(text) {
    const label = this.normalize(text);
    if (!label || this.takenLabels().has(label)) return;

    this.takenLabelsValue = [...this.takenLabelsValue, text];
  }

  takenLabels() {
    return new Set(this.takenLabelsValue.map(label => this.normalize(label)));
  }

  labels() {
    const labels = this.takenLabels();
    Object.values(this.tomSelect.options).forEach(option => labels.add(this.normalize(option.text)));

    return labels;
  }

  normalize(text) {
    return String(text ?? '').trim().toLowerCase();
  }

  // Measured against the viewport, so a fixed footer counts as occupied space.
  placeDropdown(dropdown) {
    const control = this.tomSelect.control.getBoundingClientRect();
    const needed = dropdown.offsetHeight;
    const below = window.innerHeight - control.bottom;

    this.element.classList.toggle(ABOVE_CLASS, below < needed && control.top > below);
  }

  change(value) {
    // TomSelect hides the real <select>, so a pick never produces the `change` a form emits.
    this.selectTarget.dispatchEvent(new Event('change', { bubbles: true }));

    if (value) this.clearError();
    this.syncClear();
    if (this.autoSaveUrlValue) this.autoSave.saveNow(value);

    const option = this.tomSelect.options[value];
    this.dispatch('change', {
      detail: { value: value || '', label: option ? option.text : '' },
    });
  }

  showError(message) {
    this.element.classList.add('ui-field--error');
    this.selectTarget.setAttribute('aria-invalid', 'true');

    const hint = this.element.querySelector('.ui-hint');
    if (hint) {
      hint.classList.replace('ui-hint--neutral', 'ui-hint--error');
      hint.setAttribute('role', 'alert');

      const slot = hint.querySelector('[data-hint-text]');
      if (slot) slot.textContent = message;
      hint.hidden = false;
      describeBy(this.selectTarget, this.messageIdValue, true);
    }

    this.syncAria();
  }

  // A server-rendered error otherwise stands until the next submit, over an answer that
  // no longer has anything wrong with it.
  clearError() {
    if (!this.element.classList.contains('ui-field--error')) return;

    this.element.classList.remove('ui-field--error');
    this.selectTarget.setAttribute('aria-invalid', 'false');

    const hint = this.element.querySelector('.ui-hint');
    if (hint) {
      hint.classList.replace('ui-hint--error', 'ui-hint--neutral');
      hint.removeAttribute('role');

      const text = this.element.dataset.inputFieldHint || '';
      const slot = hint.querySelector('[data-hint-text]');
      if (slot) slot.textContent = text;
      hint.hidden = text === '';
      describeBy(this.selectTarget, this.messageIdValue, text !== '');
    }

    this.syncAria();
  }

  async loadOptions(query, callback) {
    const key = query.trim().toLowerCase();
    this.failedQueries.delete(key);
    try {
      const url = new URL(this.remoteUrlValue, window.location.origin);
      url.searchParams.set('q', query);
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(response.statusText);
      callback(this.toOptions(await response.json()));
    } catch {
      this.failedQueries.add(key);
      callback();
    }
  }

  async loadGraphqlOptions(query, callback) {
    const key = query.trim().toLowerCase();
    this.failedQueries.delete(key);
    try {
      const response = await fetch(this.remoteUrlValue || '/graphql', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name=csrf-token]')?.getAttribute('content'),
        },
        body: JSON.stringify({
          query: this.graphqlQueryValue,
          variables: { query },
        }),
      });
      if (!response.ok) throw new Error(response.statusText);
      const { data, errors } = await response.json();
      if (errors || !data) throw new Error('GraphQL error');

      const nodes = this.graphqlPathValue
        .split('.')
        .reduce((node, key) => node?.[key], data);
      callback(this.toOptions(nodes || []));
    } catch {
      this.failedQueries.add(key);
      callback();
    }
  }

  toOptions(items) {
    return items.map((item) => ({
      value: String(item.value ?? item.id),
      text: item.label ?? item.text ?? item.name,
    }));
  }

  teardown() {
    this.tomSelect?.destroy();
    this.tomSelect = null;
  }
}
